/**
 * Modale de step-up TOTP au switch tenant — patch sécu Lot B.2.
 *
 * Pourquoi cette modale existe (cf docs/multitenancy.md § Switch tenant) :
 *   Pour les utilisateurs multi-tenant (Marie comptable chez plusieurs maisons),
 *   basculer entre leurs domaines via la TopBar exige un code TOTP. La session
 *   ERP active prouve déjà la possession du cookie ; le TOTP ajoute le
 *   facteur « téléphone ». Sans cette barrière, un tiers ayant accès
 *   momentanément à la machine de Marie pourrait silencieusement accéder
 *   aux données d'un autre client.
 *
 * Flow :
 *   1. L'utilisateur clique sur un domaine dans le `<TenantSwitcher>`.
 *   2. Cette modale s'ouvre, demande le code 2FA.
 *   3. Validation → appel `auth.createHandoffWithReauth` → `handoff_url` retourné.
 *   4. `window.location.assign(handoff_url)` bascule le navigateur vers le
 *      sous-domaine cible. La page courante décharge.
 *
 * UX :
 *   - Le titre rappelle le nom du domaine cible pour confirmer le choix.
 *   - Bouton « Annuler » et touche Escape ferment la modale (sauf pendant la
 *     mutation pour éviter une fermeture entre l'envoi et la redirection).
 *   - Erreurs serveur traduites : `BAD_REQUEST` → TOTP KO, `TOO_MANY_REQUESTS`
 *     → verrouillage 5/15min, `FORBIDDEN` + cause `MFA_NOT_ACTIVE` → message
 *     d'orientation vers le setup 2FA.
 */

import {
  extractTrpcCause,
  extractTrpcCode,
  NETWORK_ERROR_MESSAGE,
} from "@champ-erp/auth-primitives/web";
import {
  createHandoffWithReauthInput,
  type CreateHandoffWithReauthInput,
} from "@champ-erp/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { purgerContexteSessionLocal } from "../../auth/purger-contexte-session.js";
import { executerBasculeSessionExclusive } from "../../auth/session-handoff-lock.js";
import { trpc } from "../../lib/trpc.js";
import { compterOperationsHorsLigneNonSynchronisees } from "../../offline/index.js";

export interface SwitchTenantConfirmDialogProps {
  /** Domaine cible — affiché dans le titre pour confirmer le choix. */
  targetDomaineId: string;
  targetRaisonSociale: string;
  /** Fermeture sans confirmation (clic « Annuler », clic backdrop, touche Escape). */
  onCancel: () => void;
}

// Form values : on ne demande pas le `target_domaine_id` à l'utilisateur ; il
// est passé en prop. Le seul champ saisi est le code TOTP.
type FormValues = Pick<CreateHandoffWithReauthInput, "totp_code">;

class TravailHorsLigneEnAttenteError extends Error {
  constructor(public readonly nombreOperations: number) {
    super(`${nombreOperations} opération(s) hors ligne en attente.`);
    this.name = "TravailHorsLigneEnAttenteError";
  }
}

export function SwitchTenantConfirmDialog({
  targetDomaineId,
  targetRaisonSociale,
  onCancel,
}: SwitchTenantConfirmDialogProps) {
  const queryClient = useQueryClient();
  // Sous-schéma pour la validation côté form. Le `target_domaine_id` est
  // passé en prop (déjà validé par le parent) et n'apparaît pas dans la modale.
  const formSchema = createHandoffWithReauthInput.pick({ totp_code: true });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { totp_code: "" },
    mode: "onSubmit",
  });

  // Référence pour focus le champ TOTP au montage et après une erreur.
  const totpRef = useRef<HTMLInputElement | null>(null);

  const reauthMutation = useMutation({
    mutationFn: (input: CreateHandoffWithReauthInput) =>
      executerBasculeSessionExclusive(async () => {
        const operationsEnAttente = await compterOperationsHorsLigneNonSynchronisees();
        if (operationsEnAttente > 0) {
          throw new TravailHorsLigneEnAttenteError(operationsEnAttente);
        }

        const result = await trpc.auth.createHandoffWithReauth.mutate(input);
        // Le navigateur change physiquement de host : tous les caches React Query
        // du tenant courant sont laissés derrière (le sous-domaine cible part
        // d'un état React frais). La promise de mutation ne résout pas réellement
        // côté UI — la page va décharger.
        //
        // On purge d'abord le stockage hors-ligne (Dexie) de l'origine courante
        // avant de partir : défense en profondeur (isolation #3). Le sous-domaine
        // cible a de toute façon sa propre IndexedDB (origine distincte), mais on
        // ne laisse traîner ni parcelles ni pesées du tenant sortant.
        await purgerContexteSessionLocal(queryClient);
        return result;
      }),
    onSuccess: (result) => {
      window.location.assign(result.handoff_url);
    },
    onError: (error) => {
      if (error instanceof TravailHorsLigneEnAttenteError) {
        form.setError("totp_code", {
          message: `Synchronisez d'abord les ${error.nombreOperations} opérations hors ligne en attente. Rien n'a été supprimé.`,
        });
        return;
      }
      const code = extractTrpcCode(error);
      const cause = extractTrpcCause(error);

      if (code === "BAD_REQUEST") {
        form.setError("totp_code", { message: "Code 2FA invalide." });
        totpRef.current?.focus();
      } else if (code === "TOO_MANY_REQUESTS") {
        form.setError("totp_code", {
          message: "Trop de tentatives. Réessayez dans 15 minutes.",
        });
      } else if (code === "FORBIDDEN" && cause === "MFA_NOT_ACTIVE") {
        // Cas anormal vu la règle « MFA obligatoire si multi-tenant » — mais
        // si on arrive là, le user doit activer MFA d'abord. Message explicite.
        form.setError("totp_code", {
          message: "Activez d'abord la 2FA depuis votre profil.",
        });
      } else if (code === "FORBIDDEN") {
        form.setError("totp_code", {
          message: "Vous n'êtes plus membre actif de ce domaine.",
        });
      } else if (code === "UNAUTHORIZED") {
        form.setError("totp_code", {
          message: "Session invalide. Reconnectez-vous.",
        });
      } else {
        form.setError("totp_code", { message: NETWORK_ERROR_MESSAGE });
      }
    },
  });

  // Focus le champ TOTP au montage. Léger setTimeout pour laisser le navigateur
  // poser le DOM avant le focus (sinon certains browsers ignorent l'appel).
  useEffect(() => {
    const id = window.setTimeout(() => totpRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, []);

  // Touche Escape ferme la modale (UX standard) — sauf pendant la mutation
  // pour éviter une fermeture entre l'envoi et la redirection navigateur.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape" && !reauthMutation.isPending) {
        onCancel();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, reauthMutation.isPending]);

  const onSubmit = (values: FormValues) => {
    reauthMutation.mutate({
      target_domaine_id: targetDomaineId,
      totp_code: values.totp_code,
    });
  };

  const isPending = reauthMutation.isPending;
  const totpError = form.formState.errors.totp_code?.message;

  // Récupère la ref RHF + une ref locale pour le focus programmatique.
  const totpRegister = form.register("totp_code");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-aubergine-900/40 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="switch-tenant-title"
      // Clic backdrop ferme la modale (sauf pendant la mutation).
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-champagne-300 bg-white p-6 shadow-xl">
        <h2
          id="switch-tenant-title"
          className="text-xl font-semibold tracking-tight text-aubergine-900"
        >
          Basculer vers <span className="text-champagne-700">{targetRaisonSociale}</span>
        </h2>
        <p className="mt-2 text-sm text-neutral-700">
          Pour la sécurité, confirmez votre code 2FA avant de changer de domaine.
        </p>

        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          <div>
            <label
              htmlFor="switch-totp"
              className="mb-1.5 block text-sm font-medium text-champagne-800"
            >
              Code 2FA (6 chiffres)
            </label>
            <input
              id="switch-totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              aria-invalid={totpError ? "true" : "false"}
              {...totpRegister}
              ref={(el) => {
                totpRegister.ref(el);
                totpRef.current = el;
              }}
              className={[
                "block h-12 w-full rounded-xl border bg-white px-4 text-base tracking-[0.18em] text-champagne-900",
                "placeholder:text-champagne-800/30 focus:outline-none focus:ring-2",
                totpError
                  ? "border-red-600 focus:border-red-700 focus:ring-red-600/20"
                  : "border-champagne-800/20 focus:border-champagne-500 focus:ring-champagne-500/20",
              ].join(" ")}
            />
            {totpError && (
              <p role="alert" className="mt-1.5 text-xs font-medium text-red-700">
                {totpError}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="ui-btn-ghost inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="cta-flat inline-flex h-10 items-center justify-center rounded-xl px-5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPending ? "Vérification…" : "Basculer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
